import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import LifeWheelChart from './LifeWheelChart';
import UrlConfig from '../../../data/UrlConfig';

// --- Datos de las 12 Áreas (sin cambios) ---
const AREAS_DATA = [
  { id: 'love-relationship', title: '1. Tu Relación Amorosa', description: 'Cómo de feliz estás en tu estado de relación actual—ya sea soltero, en pareja, o deseando una.\n Si estás soltero o soltera, cuanto amor sientes por ti mismo?' },
  { id: 'friendships', title: '2. Tus Amistades', description: 'La fortaleza de tu red de apoyo. ¿Tienes al menos 5 personas que te respaldan?' },
  { id: 'finances', title: '3. Tus Finanzas', description: '¿Cuánta libertad financiera tienes para viajar, experimentar y vivir nuevas aventuras?' },
  { id: 'quality-of-life', title: '4. Tu Calidad de Vida', description: 'La calidad de tus experiencias, tu hogar, tu coche, tu espacio de trabajo. ¿Con qué frecuencia te rodeas de belleza y lujo?' },
  { id: 'health-fitness', title: '5. Tu Salud y Estado Físico', description: '¿Cómo calificarías tu salud, dada tu edad y cualquier condición física?' },
  { id: 'intellectual-life', title: '6. Tu Vida Intelectual', description: '¿Cuánto y qué tan rápido estás creciendo y aprendiendo? ¿Cuántos libros lees o cursos tomas al año?' },
  { id: 'emotional-life', title: '7. Tu Vida Emocional', description: '¿Cómo te sientes de manera constante? ¿Tus días son felices? ¿Estás en calma ante el estrés?' },
  { id: 'spiritual-life', title: '8. Tu Vida Espiritual', description: '¿Cuánto tiempo dedicas a prácticas espirituales o meditativas que te mantienen conectado y en paz?' },
  { id: 'career', title: '9. Tu Carrera', description: '¿Estás creciendo y destacando, o te sientes estancado? Si tienes un negocio, ¿está prosperando?' },
  { id: 'character', title: '10. Tu Carácter', description: '¿Quién eres como persona en lo profundo? ¿Qué tan persistente eres en vivir según tus valores?' },
  { id: 'family-parenting', title: '11. Tu Vida Familiar', description: '¿Te encanta volver a casa con tu familia? ¿Cuán cercano te sientes a ellos? (Define \'familia\' como tu lo prefieras).' },
  { id: 'life-vision', title: '12. Tu Visión de Vida', description: '¿Tienes claro hacia dónde vas? ¿Tienes una visión clara de la persona que quieres ser?' },
];

// --- Componente de Botón de Calificación (sin cambios) ---
const RatingButton = ({ score, onClick, isSelected }) => (
    <button onClick={() => onClick(score)} className={`w-10 h-10 flex items-center justify-center rounded-full border-2 transition-all duration-200 font-bold ${isSelected ? 'bg-yellow-500 border-yellow-400 text-black scale-110' : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-yellow-500 hover:text-white'}`}>
    {score}
  </button>
);

// --- Componente de Resumen (sin cambios) ---
const ActivitySummary = ({ ratings, nextActivity, onRedo, onProceed }) => {
    const sortedAreas = useMemo(() => {
        return Object.entries(ratings)
            .map(([areaId, score]) => ({ ...AREAS_DATA.find(area => area.id === areaId), score }))
            .sort((a, b) => a.score - b.score);
    }, [ratings]);

    const lowestThree = sortedAreas.slice(0, 3);
    const highestThree = sortedAreas.slice(-3).reverse();

    return(
        <div className="bg-gray-900 rounded-lg animate-fade-in p-4 sm:p-6">
            <h2 className="text-3xl font-bold text-yellow-400 mb-6 text-center">Resumen de Tu Rueda de la Vida</h2>
            <div className="mb-8">
                <LifeWheelChart ratings={ratings} areasData={AREAS_DATA} />
            </div>
            <p className="text-center text-gray-400 mb-8">Este gráfico representa visualmente tu nivel de satisfacción en cada área. Las áreas más cercanas al centro son tus mayores oportunidades de crecimiento.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                    <h3 className="text-xl font-semibold text-green-400 mb-4">Tus 3 Áreas más Fuertes</h3>
                    <ul className="space-y-3 text-left">
                        {highestThree.map(area => (<li key={area.id} className="p-3 bg-gray-800 rounded-md flex justify-between items-center"><span className="text-gray-200">{area.title.replace(/^\d+\.\s*/, '')}</span><span className="font-bold text-xl text-green-400">{area.score}</span></li>))}
                    </ul>
                </div>
                <div>
                    <h3 className="text-xl font-semibold text-red-400 mb-4">Tus 3 Áreas de Oportunidad</h3>
                     <ul className="space-y-3 text-left">
                        {lowestThree.map(area => (<li key={area.id} className="p-3 bg-gray-800 rounded-md flex justify-between items-center"><span className="text-gray-200">{area.title.replace(/^\d+\.\s*/, '')}</span><span className="font-bold text-xl text-red-400">{area.score}</span></li>))}
                    </ul>
                </div>
            </div>
            <p className="mt-8 text-gray-400 text-center">¡Felicidades por completar el diagnóstico! Ahora tienes una línea base clara.</p>
            <div className="mt-10 flex flex-col sm:flex-row justify-center items-center gap-4">
                <button onClick={onRedo} className="w-full sm:w-auto bg-gray-700 text-gray-300 font-bold py-3 px-8 rounded-lg hover:bg-gray-600 transition-all duration-300">
                    Rehacer Actividad
                </button>
                {nextActivity && (
                    <button onClick={onProceed} className="w-full sm:w-auto bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-400 transition-all duration-300">
                        Ir a la Siguiente Actividad →
                    </button>
                )}
            </div>
            <div className="text-center mt-6">
                 <Link to="/cursos/manifiesta" className="text-sm text-gray-500 hover:text-yellow-400 transition-colors">Volver al Temario sin avanzar</Link>
            </div>
        </div>
    )
};

// --- Componente de Introducción (sin cambios) ---
const ActivityIntro = ({ onStart }) => (
    <div className="bg-gray-900 rounded-lg p-6 md:p-8 animate-fade-in text-gray-300 max-w-3xl mx-auto">
        <h3 className="text-2xl font-bold text-yellow-400 mb-4">El Primer Paso: Tu Diagnóstico de Vida</h3>
        <p className="mb-4">Para manifestar la vida que deseas, primero necesitas saber con claridad dónde te encuentras ahora. Muchas personas se enfocan intensamente en un solo aspecto de sus vidas, como la carrera o el dinero, y sin darse cuenta, descuidan su salud, sus relaciones o su paz interior.</p>
        <p className="mb-4">Una vida verdaderamente extraordinaria es una vida en equilibrio. Este ejercicio, conocido como la "Rueda de la Vida", es una herramienta poderosa para obtener una fotografía honesta y completa de tu momento actual. Te ayuda a ver qué áreas están floreciendo y cuáles necesitan más de tu atención y energía.</p>
        <p className="font-semibold text-yellow-300 mb-6">A continuación, te presentaremos 12 áreas fundamentales de la vida. Tu tarea es simple: califica tu nivel de satisfacción en cada una, en una escala de 0 a 10. No lo analices demasiado; confía en tu intuición y responde con lo primero que sientas. Este es tu punto de partida para una transformación consciente.</p>
        <button onClick={onStart} className="w-full bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-400 transition-transform transform hover:scale-105">
            Comenzar mi Diagnóstico
        </button>
    </div>
);


const TwelveAreasActivity = ({ nextActivity, onProceed }) => {
  const { user, getGuestActivityData, saveGuestActivityProgress, logout } = useAuth();
  const activityId = '12-areas-balance';
  const navigate = useNavigate();

  // <-- MODIFICADO: Se elimina la línea `const urlConfig = UrlConfig();`

  const [ratings, setRatings] = useState({});
  const [currentAreaIndex, setCurrentAreaIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activityStarted, setActivityStarted] = useState(false);
  const [lastRatedAreaId, setLastRatedAreaId] = useState(null);

  const currentArea = AREAS_DATA[currentAreaIndex];
  const totalRated = Object.keys(ratings).length;
  
  const handleApiError = async (response) => {
    if (response.status === 401) {
      await logout();
      alert("Tu sesión ha expirado. Por favor, inicia sesión de nuevo.");
      navigate('/login');
      return true;
    }
    return false;
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      let loadedRatings = {};

      if (user) {
        try {
          // <-- MODIFICADO: Se usa UrlConfig directamente y '&' para el parámetro
          const response = await fetch(`${UrlConfig.activity_getRatings}&activityId=${activityId}`, { credentials: 'include' });
          if (await handleApiError(response)) return;
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data) loadedRatings = result.data;
          }
        } catch (error) { console.error("Error al cargar datos de API:", error); }
      } else {
        const guestData = getGuestActivityData(activityId);
        if (guestData) loadedRatings = guestData;
      }
      
      setRatings(loadedRatings);
      if (Object.keys(loadedRatings).length === AREAS_DATA.length) setIsCompleted(true);
      setIsLoading(false);
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activityId, getGuestActivityData]);

  const handleRatingSelect = async (score) => {
    const originalRatings = ratings;
    const newRatings = { ...ratings, [currentArea.id]: score };
    setRatings(newRatings);
    setLastRatedAreaId(currentArea.id);

    if (!user) {
        if (currentAreaIndex < AREAS_DATA.length - 1) setCurrentAreaIndex(currentAreaIndex + 1);
        return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      // <-- MODIFICADO: Se usa UrlConfig directamente
      const response = await fetch(UrlConfig.activity_saveRating, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ activityId, areaId: currentArea.id, score }),
      });

      if (await handleApiError(response)) {
          setRatings(originalRatings);
          return;
      }
      
      if (!response.ok) throw new Error('Fallo al guardar la calificación.');

      if (currentAreaIndex < AREAS_DATA.length - 1) setCurrentAreaIndex(currentAreaIndex + 1);
    } catch (error) { 
        console.error("Error al guardar en API:", error);
        alert('No se pudo guardar tu calificación. Por favor, inténtalo de nuevo.');
        setRatings(originalRatings);
    } 
    finally { 
        setIsSubmitting(false); 
    }
  };

  const handleFinishActivity = async () => {
    if (!user) {
      saveGuestActivityProgress(activityId, ratings);
      setIsCompleted(true);
      return;
    }

    try {
      // <-- MODIFICADO: Se usa UrlConfig directamente
      const response = await fetch(UrlConfig.activity_markAsComplete, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ activityId })
      });
      
      if (await handleApiError(response)) return;
      if(response.ok) setIsCompleted(true);
      else alert("Hubo un problema al finalizar la actividad. Inténtalo de nuevo.");
    } catch (error) { 
        console.error("Error al marcar como completada:", error);
        alert("Hubo un problema al finalizar la actividad. Inténtalo de nuevo.");
    }
  };

  const handleRedoActivity = () => {
    setIsCompleted(false);
    setActivityStarted(true); 
    setCurrentAreaIndex(0);
  };
  
  const handleStartActivity = () => setActivityStarted(true);

  const goToNext = () => currentAreaIndex < AREAS_DATA.length - 1 && setCurrentAreaIndex(currentAreaIndex + 1);
  const goToPrev = () => currentAreaIndex > 0 && setCurrentAreaIndex(currentAreaIndex - 1);
  const handleAreaSelectById = (areaId) => {
    const newIndex = AREAS_DATA.findIndex(area => area.id === areaId);
    if (newIndex !== -1) setCurrentAreaIndex(newIndex);
  };

  const sortedAreasForDisplay = useMemo(() => {
    if (!lastRatedAreaId) {
      return AREAS_DATA;
    }
    const lastRated = AREAS_DATA.find(area => area.id === lastRatedAreaId);
    const others = AREAS_DATA.filter(area => area.id !== lastRatedAreaId);
    return [lastRated, ...others];
  }, [lastRatedAreaId]);


  if (isLoading) return <div className="text-center p-10 text-yellow-400 animate-pulse">Cargando tu viaje...</div>;
  if (isCompleted) return <ActivitySummary ratings={ratings} nextActivity={nextActivity} onRedo={handleRedoActivity} onProceed={onProceed} />;
  if (!activityStarted) return <ActivityIntro onStart={handleStartActivity} />;

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <div className="w-full lg:w-2/3">
        <div className="p-6 bg-gray-900 rounded-lg text-center">
          <div className="mb-6">
            <h3 className="text-3xl font-bold text-white mt-2">{currentArea.title.replace(/^\d+\.\s*/, '')}</h3>
            <p className="text-gray-400 mt-3 max-w-xl mx-auto whitespace-pre-line">{currentArea.description}</p>
          </div>
          <p className="mb-4 text-gray-300">En una escala de 0 a 10, ¿cómo calificarías esta área?</p>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {Array.from({ length: 11 }, (_, i) => i).map(score => (
              <RatingButton key={score} score={score} onClick={() => handleRatingSelect(score)} isSelected={ratings[currentArea.id] === score} />
            ))}
          </div>
          {isSubmitting && <p className="mt-4 text-sm text-yellow-400 animate-pulse">Guardando...</p>}
          <div className="mt-8 pt-6 border-t border-gray-700 flex justify-between items-center">
            <button onClick={goToPrev} disabled={currentAreaIndex === 0} className="bg-gray-700 text-white font-bold py-2 px-6 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600">Anterior</button>
            {totalRated === AREAS_DATA.length ? (
              <button onClick={handleFinishActivity} className="bg-green-500 text-black font-bold py-2 px-6 rounded-lg hover:bg-green-400">Finalizar y ver Resumen</button>
            ) : (
              <button onClick={goToNext} disabled={currentAreaIndex === AREAS_DATA.length - 1} className="bg-yellow-500 text-black font-bold py-2 px-6 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-yellow-400">Siguiente</button>
            )}
          </div>
        </div>
      </div>
      <div className="w-full lg:w-1/3 lg:pl-8">
            <h3 className="text-xl font-bold text-yellow-400 mb-4">Tu Progreso</h3>
            <div className="space-y-2">
                {sortedAreasForDisplay.map((area) => (
                    <button key={area.id} onClick={() => handleAreaSelectById(area.id)} className={`w-full text-left p-3 rounded-md transition-colors duration-200 flex justify-between items-center ${currentArea.id === area.id ? 'bg-yellow-500/20 border-l-4 border-yellow-500' : 'bg-gray-800/50 hover:bg-gray-700/50'}`}>
                        <span className={`font-semibold ${currentArea.id === area.id ? 'text-white' : 'text-gray-300'}`}>{area.title.replace(/^\d+\.\s*/, '')}</span>
                        {ratings[area.id] !== undefined && (
                            <span className="text-xl font-black rounded-full w-8 h-8 flex items-center justify-center bg-yellow-500 text-black">
                                {ratings[area.id]}
                            </span>
                        )}
                    </button>
                ))}
            </div>
      </div>
    </div>
  );
};

export default TwelveAreasActivity;